import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, FileText, GraduationCap, Download, Calendar, Loader2, ScrollText, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const HistoricalRecordPage = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [expandedYear, setExpandedYear] = useState(null);

  useEffect(() => {
    api.get('/my-historical-records')
      .then(res => setRecords(res.data))
      .catch(() => toast.error('فشل تحميل السجل التاريخي'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (year) => {
    setDownloading(year);
    try {
      const res = await api.get(`/my-historical-records/download/${year}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `منجزات_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('تم تحميل الملف بنجاح');
    } catch {
      toast.error('فشل تحميل الملف');
    } finally { setDownloading(null); }
  };

  const ItemsTable = ({ items, type }) => {
    if (!items || items.length === 0) return null;
    const typeLabels = { books: 'الكتب', articles: 'المقالات', studies: 'الدراسات العلمية' };
    const TypeIcon = type === 'books' ? BookOpen : type === 'articles' ? FileText : GraduationCap;

    return (
      <div className="mt-4">
        <h4 className="text-lg font-bold text-accent flex items-center gap-2 mb-3">
          <TypeIcon className="w-5 h-5 text-primary" />
          {typeLabels[type]} ({items.length})
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid={`table-${type}`}>
            <thead>
              <tr className="bg-primary/10">
                <th className="text-right p-3 font-semibold text-accent border border-primary/20">العنوان</th>
                <th className="text-right p-3 font-semibold text-accent border border-primary/20">المؤلف</th>
                <th className="text-center p-3 font-semibold text-accent border border-primary/20">الصفحات</th>
                <th className="text-center p-3 font-semibold text-accent border border-primary/20">الختم</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-muted/30'}>
                  <td className="p-3 border border-primary/10 font-medium">{item.title}</td>
                  <td className="p-3 border border-primary/10">{item.author}</td>
                  <td className="p-3 border border-primary/10 text-center">{item.pages}</td>
                  <td className="p-3 border border-primary/10 text-center">{item.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const chartData = useMemo(() => {
    if (records.length === 0) return [];
    return [...records].sort((a, b) => a.year.localeCompare(b.year)).map(r => ({
      name: `${r.season_name}\n(${r.year}هـ)`,
      books: r.books?.length || 0,
      articles: r.articles?.length || 0,
      studies: r.studies?.length || 0,
      pages: (r.books || []).concat(r.articles || [], r.studies || []).reduce((s, i) => s + (parseInt(i.pages) || 0), 0),
    }));
  }, [records]);

  const totalAll = useMemo(() => {
    return records.reduce((acc, r) => ({
      books: acc.books + (r.books?.length || 0),
      articles: acc.articles + (r.articles?.length || 0),
      studies: acc.studies + (r.studies?.length || 0),
      pages: acc.pages + (r.books || []).concat(r.articles || [], r.studies || []).reduce((s, i) => s + (parseInt(i.pages) || 0), 0),
    }), { books: 0, articles: 0, studies: 0, pages: 0 });
  }, [records]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white p-3 rounded-xl shadow-lg border border-primary/20 text-sm">
        <p className="font-bold text-accent mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: {p.value}</p>
        ))}
      </div>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-16 h-16 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="historical-record-page">
      <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center gap-4">
          <ScrollText className="w-10 h-10" />
          <div>
            <h1 className="text-3xl font-bold">السجل التاريخي</h1>
            <p className="text-white/80 mt-1">منجزاتك القرائية من المواسم السابقة</p>
          </div>
        </div>
      </div>

      {records.length === 0 ? (
        <Card className="p-12 text-center">
          <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-xl text-muted-foreground">لا توجد سجلات تاريخية لك حتى الآن</p>
          <p className="text-sm text-muted-foreground mt-2">سيتم إضافة سجلاتك عند رفع بيانات المواسم من قبل الإدارة</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Performance Comparison Chart */}
          {chartData.length > 0 && (
            <Card className="p-6" data-testid="performance-chart">
              <h3 className="text-xl font-bold text-accent flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                مقارنة الأداء بين المواسم
              </h3>
              <p className="text-sm text-muted-foreground mb-4">عدد المنجزات القرائية حسب النوع لكل موسم</p>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-[#C19A6B]/10 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-[#C19A6B]">{totalAll.books}</p>
                  <p className="text-xs text-muted-foreground">إجمالي الكتب</p>
                </div>
                <div className="bg-[#5c8a6b]/10 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-[#5c8a6b]">{totalAll.articles}</p>
                  <p className="text-xs text-muted-foreground">إجمالي المقالات</p>
                </div>
                <div className="bg-[#7c5e99]/10 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-[#7c5e99]">{totalAll.studies}</p>
                  <p className="text-xs text-muted-foreground">إجمالي الدراسات</p>
                </div>
                <div className="bg-primary/10 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{totalAll.pages.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">إجمالي الصفحات</p>
                </div>
              </div>

              <div className="h-72" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} barGap={4} barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#5c3d2e' }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#5c3d2e' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      formatter={(value) => <span style={{ fontSize: 13, color: '#5c3d2e' }}>{value}</span>}
                    />
                    <Bar dataKey="books" name="الكتب" fill="#C19A6B" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="articles" name="المقالات" fill="#5c8a6b" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="studies" name="الدراسات" fill="#7c5e99" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
          {records.map((record) => {
            const isExpanded = expandedYear === record.year;
            const totalItems = (record.books?.length || 0) + (record.articles?.length || 0) + (record.studies?.length || 0);

            return (
              <Card key={record.year} className="overflow-hidden" data-testid={`record-${record.year}`}>
                <div
                  className="p-5 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedYear(isExpanded ? null : record.year)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white">
                        <Calendar className="w-7 h-7" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-accent">{record.season_name} ({record.year}هـ)</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          {record.books?.length > 0 && (
                            <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-primary" /> {record.books.length} كتاب</span>
                          )}
                          {record.articles?.length > 0 && (
                            <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-primary" /> {record.articles.length} مقالة</span>
                          )}
                          {record.studies?.length > 0 && (
                            <span className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5 text-primary" /> {record.studies.length} دراسة</span>
                          )}
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                            {totalItems} منجز
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDownload(record.year); }}
                        disabled={downloading === record.year}
                        className="text-primary border-primary/30 hover:bg-primary/10"
                        data-testid={`download-${record.year}`}
                      >
                        {downloading === record.year ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><Download className="w-4 h-4 ml-1" /> تحميل PDF</>
                        )}
                      </Button>
                      <span className={`text-xl transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                        &#x25BC;
                      </span>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-primary/10">
                    <ItemsTable items={record.books} type="books" />
                    <ItemsTable items={record.articles} type="articles" />
                    <ItemsTable items={record.studies} type="studies" />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HistoricalRecordPage;
